/**
 * Minimal Straight Edge with Delete Button
 *
 * Clean, minimal edge with straight lines and rounded corners.
 */

import React from "react";
import {
  EdgeLabelRenderer,
  type EdgeProps,
  getSmoothStepPath,
  useReactFlow,
} from "@xyflow/react";
import { X } from "lucide-react";

export const DeleteButtonEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
}) => {
  const { setEdges } = useReactFlow();
  const [isHovered, setIsHovered] = React.useState(false);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  const isActive = isHovered || selected;
  // Set by the editor while the downstream node is executing — animates the
  // connection so data visibly "flows" into the running node, like n8n.
  const isFlowing = !!(data as any)?.isActive;
  const strokeColor = isFlowing ? "#3b82f6" : isActive ? "#6366f1" : "#aba9a9ff";

  const onEdgeClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  };

  return (
    <>
      {/* Main edge line */}
      <path
        d={edgePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth={isFlowing ? 2.5 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transition: "stroke 0.15s, stroke-width 0.15s",
        }}
      />

      {/* Animated "flowing data" overlay while the target node runs */}
      {isFlowing && (
        <path
          d={edgePath}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 6,
            // `dashdraw` keyframe ships with @xyflow/react's stylesheet.
            animation: "dashdraw 0.5s linear infinite",
          }}
        />
      )}

      {/* Invisible interaction path */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ cursor: "pointer", pointerEvents: "stroke" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: "all",
          }}
          className="nodrag nopan"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={onEdgeClick}
            className={`w-5 h-5 flex items-center justify-center rounded-full transition-all duration-150 ${
              isHovered
                ? "bg-red-500 text-white scale-100 opacity-100 shadow-sm"
                : "scale-0 opacity-0"
            }`}
            aria-label="Delete"
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};
